import type { ButtonProps as Props } from './types';
import { NANP_DIGITS_REGEX } from './types';
import type { MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { cls } from '@/utils/cls';
import { useColorProvider } from '@/utils/ColorProvider';
import { usePostHog } from '@/lib/posthogProvider';
import { useDynamicPhoneNumber } from '@/utils';
import { buildUrlWithParams } from '@/utils/buildUrlWithParams';
import style from './Button.module.scss';
export default function Button({
  variant = 'Default',
  extendStyles,
  copy,
  desktopCopy,
  mobileCopy,
  color,
  arrow,
  href = '#form-container',
  buttonAlignment,
  textColor,
  usePageColors,
  scrollTo,
  hover,
  wideButton,
  logPostHogEvent,
  buttonId,
  phoneNumber,
  showDynamicPhoneNumber = false,
  // Filter out Builder.io specific props
  builderState: _builderState,
  builderBlock: _builderBlock
}: Props) {
  const isPhoneCTA = variant === 'Phone CTA';
  const {
    dynamicPhoneNumber,
    loading
  } = useDynamicPhoneNumber({
    showDynamicPhoneNumber: isPhoneCTA && showDynamicPhoneNumber
  });
  const buttonAlignmentClass = buttonAlignment?.toLowerCase();
  const _className = cls(style.root, extendStyles, style[buttonAlignmentClass], !!hover ? style.hoverColor : '', wideButton && style.wide);
  const {
    colors
  } = useColorProvider();
  const _backgroundColor = usePageColors ? colors.secondaryAccentColor : color;
  const posthog = usePostHog();
  const router = useRouter();
  if (isPhoneCTA && showDynamicPhoneNumber && loading) return null;

  // ── Phone number resolution (Phone CTA only) ─────────────────────
  const rawPhone = isPhoneCTA ? (showDynamicPhoneNumber ? dynamicPhoneNumber ?? phoneNumber : phoneNumber) || null : null;
  const phoneDigits = rawPhone?.replace(/\D/g, '') ?? '';
  const normalizedDigits = phoneDigits.length === 11 && phoneDigits.startsWith('1') ? phoneDigits.slice(1) : phoneDigits;
  const formattedPhone = NANP_DIGITS_REGEX.test(normalizedDigits) ? `(${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(3, 6)}-${normalizedDigits.slice(6)}` : null;
  const resolvedHref = formattedPhone ? `tel:+1${normalizedDigits}` : href;

  // Forward the current page's tracking params onto real navigations so
  // attribution survives the hop (no-op for tel:, mailto:, and #anchor hrefs).
  const linkHref = buildUrlWithParams(resolvedHref, router?.query ?? {});

  // ── Copy resolution ──────────────────────────────────────────────
  const phoneToken = /\{\{phoneNumber\}\}/g;
  const phoneReplacement = formattedPhone ?? '';
  let buttonText: string;
  if (isPhoneCTA) {
    buttonText = (desktopCopy ?? '').replace(phoneToken, phoneReplacement).replace(/\s+/g, ' ').trim();
  } else {
    buttonText = copy ?? '';
  }

  // ── Handlers ─────────────────────────────────────────────────────
  function smoothScroll(e: MouseEvent<HTMLElement>) {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({
      behavior: 'smooth'
    });
  }
  function handleClick(e: MouseEvent<HTMLElement>) {
    if (logPostHogEvent && buttonId) {
      posthog.capture('button_clicked', {
        type: 'cta',
        button_id: buttonId,
        button_text: buttonText,
        // Clean href, not linkHref — keeps this property groupable in PostHog.
        button_url: resolvedHref,
        ...(isPhoneCTA && formattedPhone && {
          dynamic_phone_number: formattedPhone
        })
      });
    }
    if (!isPhoneCTA && scrollTo) {
      smoothScroll(e);
    }
  }

  // ── Render ────────────────────────────────────────────────────────
  const buttonStyle = {
    backgroundColor: _backgroundColor,
    color: textColor,
    '--hover-color': hover
  } as React.CSSProperties;
  const buttonContent = <>
			{isPhoneCTA && mobileCopy ? <>
					<span className={style.desktopCopy}>{buttonText}</span>
					<span className={style.mobileCopy}>{mobileCopy}</span>
				</> : buttonText}
			{!!arrow && <svg className={style.icon} viewBox="0 0 20 20" role="presentation">
					<path d="M2 10L18 10" />
					<path d="M10 2L18 10L10 18" />
				</svg>}
		</>;
  return <a className={_className} href={linkHref} style={buttonStyle} onClick={handleClick} data-sentry-component="Button" data-sentry-source-file="Button.tsx">
			{buttonContent}
		</a>;
}